<script lang="ts">
  /**
   * Replaces three duplicated implementations: FamilyCard's own BADGE_TONES map, the inline
   * text-[11px] px-1.5 py-0.5 copies scattered across a few other dialogs, and the pill-shaped
   * language/category filter buttons. One component, because a static label and a toggleable filter
   * chip are the same visual object with different affordances — so the affordance is what decides
   * the shape here, not a separate prop nobody would remember to set consistently: passing onclick
   * turns the tight "tag" (rounded, text-[11px]) into a roomier "pill" (rounded-full, text-xs) with a
   * hover state, and selected picks it out as the active one among siblings. Every existing call site
   * already drew exactly this line — pills were always the clickable ones, tags were always static.
   */
  import type { Snippet } from "svelte";

  let {
    tone = "neutral",
    selected = false,
    onclick,
    title,
    children,
    ...rest
  }: {
    tone?: "accent" | "success" | "warning" | "error" | "muted" | "neutral";
    /** Only meaningful alongside onclick — the pressed state among a row of filter chips. */
    selected?: boolean;
    onclick?: (event: MouseEvent) => void;
    title?: string;
    children?: Snippet;
    [key: string]: unknown;
  } = $props();

  const TONES = {
    accent: "border-accent text-accent",
    success: "border-success text-success",
    warning: "border-warning text-warning",
    error: "border-error text-error",
    muted: "border-line text-ink-muted",
    neutral: "border-line text-ink-secondary",
  } as const;
</script>

<svelte:element
  this={onclick ? "button" : "span"}
  type={onclick ? "button" : undefined}
  {title}
  {onclick}
  {...rest}
  class="inline-flex items-center gap-1 border whitespace-nowrap transition-colors duration-200 ease-out
         {onclick ? 'rounded-full text-xs px-2.5 py-1 cursor-pointer' : 'rounded text-[11px] px-1.5 py-0.5'}
         {selected
           ? 'bg-accent text-white border-accent'
           : `bg-panel ${TONES[tone]} ${onclick ? 'hover:border-accent hover:text-accent' : ''}`}"
>
  {@render children?.()}
</svelte:element>
